import { css, html, nothing } from 'lit';
import { api } from '../../lib/api.js';
import {
    assetTypeLabel,
    cannotCommission,
    commissionSlugFor,
    defaultRateCents,
    describeProperties,
    priorityMeta,
} from '../../lib/art-pipeline.js';
import { BaseComponent } from '../../lib/base-component.js';
import { briefHTML } from '../../lib/brief.js';
import { BRIEF_STYLES } from '../../lib/brief-styles.js';

/**
 * The "+ Commission" form, over the pipeline (issue #195): a commission is a
 * job on a spec, so it asks only for the job's own fields (slug, rate,
 * whether any freelancer may claim it) and shows the spec it's for. Cancel
 * leaves the pipeline as it was.
 *
 * Fires `commission-created` (detail: the new commission) and `close`.
 */
class PipelineCommissionForm extends BaseComponent {
    static properties = {
        spec: { type: Object, attribute: false },
        _slug: { type: String, state: true },
        _rate: { type: String, state: true },
        _claimable: { type: Boolean, state: true },
        _saving: { type: Boolean, state: true },
        _error: { type: String, state: true },
    };

    static styles = [
        BRIEF_STYLES,
        css`
        :host { display: block; }
        .modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 9999; }
        .modal-panel { background: white; border-radius: 8px; width: 90vw; max-width: 520px; max-height: 90vh; display: flex; flex-direction: column; box-shadow: 0 1rem 3rem rgba(0,0,0,0.2); }
        .modal-header { display: flex; align-items: center; justify-content: space-between; padding: 1rem 1.25rem; border-bottom: 1px solid #dee2e6; }
        .modal-header h5 { margin: 0; font-weight: 600; }
        .modal-body { padding: 1rem 1.25rem; overflow-y: auto; font-size: 0.9rem; }
        .modal-footer { display: flex; justify-content: flex-end; gap: 0.5rem; padding: 0.75rem 1.25rem; border-top: 1px solid #dee2e6; }
        .spec-summary { background: #f8f9fa; border: 1px solid #e5e7eb; border-radius: 6px; padding: 0.75rem; margin-bottom: 1rem; }
        .spec-summary .name { font-weight: 600; }
        .spec-summary .meta { display: flex; flex-wrap: wrap; gap: 0.4rem; align-items: center; color: #555; font-size: 0.8rem; margin: 0.25rem 0 0.5rem; }
        .spec-summary .brief { color: #444; font-size: 0.85rem; max-height: 6.5rem; overflow: hidden; }
        .spec-summary .brief h1, .spec-summary .brief h2, .spec-summary .brief h3,
        .spec-summary .brief h4, .spec-summary .brief h5, .spec-summary .brief h6 { font-size: 0.85rem; margin: 0 0 0.1rem; }
        .row-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 0.75rem; }
        .form-label { font-size: 0.8rem; font-weight: 600; margin-bottom: 0.25rem; }
        .optional { font-weight: 400; color: #888; }
    `,
    ];

    constructor() {
        super();
        this.spec = null;
        this._slug = '';
        this._rate = '';
        this._claimable = false;
        this._saving = false;
        this._error = null;
    }

    connectedCallback() {
        super.connectedCallback();
        if (this.spec) this._slug = commissionSlugFor(this.spec);
    }

    firstUpdated() {
        /** @type {HTMLInputElement} */ (
            this.shadowRoot.querySelector('#commission-slug')
        )?.focus();
    }

    _close() {
        this.dispatchEvent(new CustomEvent('close'));
    }

    get _defaultRateCents() {
        return defaultRateCents(this.spec.asset_type, this.spec.properties?.size);
    }

    async _submit() {
        const slug = this._slug.trim();
        if (!slug) {
            this._error = 'Give the commission a slug.';
            return;
        }
        let rateCents = null;
        if (this._rate.trim()) {
            const dollars = Number(this._rate);
            if (!Number.isFinite(dollars) || dollars < 0) {
                this._error = 'The rate must be a dollar amount, like 50 or 49.50.';
                return;
            }
            rateCents = Math.round(dollars * 100);
        }
        this._saving = true;
        this._error = null;
        try {
            const created = await api.post(`/api/specs/${this.spec.id}/commissions`, {
                slug,
                rate_cents: rateCents,
                claimable: this._claimable,
            });
            this.dispatchEvent(new CustomEvent('commission-created', { detail: created }));
            this._close();
        } catch (err) {
            this._error = err.message;
        } finally {
            this._saving = false;
        }
    }

    render() {
        const spec = this.spec;
        if (!spec) return nothing;
        const blocked = cannotCommission(spec);
        const priority = priorityMeta(spec.priority);
        const props = describeProperties(spec);
        const defaultRate = this._defaultRateCents;
        return html`
            <div class="modal-overlay" @click="${(e) => e.target === e.currentTarget && this._close()}">
                <div class="modal-panel" role="dialog" aria-label="New commission"
                    @keydown="${(e) => e.key === 'Escape' && this._close()}">
                    <div class="modal-header">
                        <h5>New Commission</h5>
                        <button class="btn-close" aria-label="Close" @click="${this._close}"></button>
                    </div>
                    <form class="modal-body" @submit="${(e) => {
                        e.preventDefault();
                        if (!blocked) this._submit();
                    }}">
                        ${this._error ? html`<div class="alert alert-danger py-2 small">${this._error}</div>` : nothing}
                        <div class="spec-summary">
                            <div class="name">${spec.name}</div>
                            <div class="meta">
                                <span>${assetTypeLabel(spec.asset_type)}${props ? ` · ${props}` : ''}</span>
                                <span class="badge ${priority.badge}">${priority.label}</span>
                                <span>${spec.reference_images.length} reference image${spec.reference_images.length === 1 ? '' : 's'}</span>
                                ${spec.commissions.length ? html`<span>· already ${spec.commissions.length} commission${spec.commissions.length === 1 ? '' : 's'}</span>` : nothing}
                            </div>
                            ${spec.brief?.trim() ? html`<div class="brief brief-md" .innerHTML="${briefHTML(spec.brief)}"></div>` : html`<div class="brief brief-md"><span class="text-muted">No brief yet.</span></div>`}
                        </div>
                        ${blocked ? html`<div class="alert alert-warning py-2 small">${blocked}</div>` : nothing}
                        <div class="mb-3">
                            <label class="form-label" for="commission-slug">Slug *</label>
                            <input id="commission-slug" class="form-control" required ?disabled="${Boolean(blocked)}"
                                .value="${this._slug}" @input="${(e) => (this._slug = e.target.value)}">
                            <div class="form-text">Lowercase letters, numbers and hyphens. It names the commission everywhere.</div>
                        </div>
                        <div class="row-2">
                            <div>
                                <label class="form-label" for="commission-rate">Rate (USD) <span class="optional">(optional)</span></label>
                                <input id="commission-rate" class="form-control" inputmode="decimal" ?disabled="${Boolean(blocked)}"
                                    placeholder="${defaultRate != null ? `Default $${(defaultRate / 100).toFixed(2)}` : 'No default'}"
                                    .value="${this._rate}" @input="${(e) => (this._rate = e.target.value)}">
                            </div>
                            <div class="d-flex align-items-end">
                                <div class="form-check mb-2">
                                    <input id="commission-claimable" class="form-check-input" type="checkbox" ?disabled="${Boolean(blocked)}"
                                        .checked="${this._claimable}" @change="${(e) => (this._claimable = e.target.checked)}">
                                    <label class="form-check-label" for="commission-claimable">Any freelancer may claim it</label>
                                </div>
                            </div>
                        </div>
                        <button type="submit" hidden></button>
                    </form>
                    <div class="modal-footer">
                        <button class="btn btn-outline-secondary" @click="${this._close}">Cancel</button>
                        <button class="btn btn-primary" ?disabled="${this._saving || Boolean(blocked)}" @click="${this._submit}">Create commission</button>
                    </div>
                </div>
            </div>
        `;
    }
}

customElements.define('pipeline-commission-form', PipelineCommissionForm);
